import React, { useEffect, useMemo, useState } from 'react';

import { useApiRequest } from 'src/hooks/useApi';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AppLayout from 'src/routes/layouts/AppLayout';
import useStyles from 'src/styles/di-theme';
import classNames from 'classnames';
import {
	Typography,
	Button,
	Box,
	Alert,
} from '@barracuda-internal/bds-core-v2';
import { getAppConfig } from 'src/AppLoader';

import {
	ExitToApp,
	Dashboard,
	List,
	Settings,
	Description,
} from '@barracuda-internal/bds-core-v2/dist/Icons/Core';
import { BarChart } from '@barracuda-internal/bds-core-v2/dist/Icons/Charts';
import jwtDecode from 'jwt-decode';
import SupportPopup from './SupportPopup';
import { BDSProvider } from '@barracuda-internal/bds-core-v2';
import NavBarContainer from './NavBarContainer';
import { Outlet } from 'react-router-dom';

interface AppConfig {
	codeusEnvironment: 'prod' | 'prodin' | 'dev' | 'dvin' | 'local'; // Define possible values for the environment
}

function SideNav(): React.ReactElement {
	const [appConfig, setAppConfig] = useState<AppConfig | null>(null); // State to store app config
	const { api } = useAppToolchain();

	const classes = useStyles();
	const { auth } = useAuth();
	const { config } = useAppToolchain();
	const [refreshDelegate, setRefreshDelegate] = React.useState(0);
	const [resStatus, supportDelegate, error] = useApiRequest(
		() => {
			return api.getSupportDelegateToken();
		},
		[auth, refreshDelegate],
		() => {},
	);

	const [showPopup, setShowPopup] = React.useState(false);
	const [removeAccess, setRemoveAccess] = React.useState(0);

	const isAdmin = auth?.isFullAdmin();

	const mainMenu = useMemo(() => {
		if (isAdmin) {
			return [
				{
					path: '/detections',
					icon: <Dashboard />,
					name: 'Detections',
				},
				{
					path: '/auditlog',
					icon: <List />,
					name: 'Audit Logs',
				},
				{
					path: '/scanlog',
					icon: <Description />,
					name: 'Scan Logs',
				},

				{
					path: '/reports',
					name: 'Reports',
					icon: <BarChart />,
					children: [
						{
							name: 'Report List',
							path: '/reports',
						},
						{
							name: 'Scheduled Reports',
							path: '/reports/scheduled-reports',
						},
					],
				},

				{
					path: '/settings',
					name: 'Settings',
					icon: <Settings />,
					setExpanded: true,
					children: [
						{
							name: 'Connectors',
							path: '/settings/connectors',
						},
						{
							name: 'Policies',
							path: '/settings/policy',
						},
						{
							name: 'Classifiers',
							path: '/settings/classifiers',
						},
						{
							name: 'Notifications',
							path: '/settings/Notifications',
						},

						{
							name: 'Users',
							path: '/settings/users',
						},
						{
							name: 'Manage Tenant',
							path: '/settings/manage',
						},
					],
				},
			];
		} else {
			return [
				{
					path: '/detections',
					icon: <Dashboard />,
					name: 'Detections',
				},
				{
					path: '/settings',
					name: 'Settings',
					icon: <Settings />,
					children: [
						{
							name: 'Manage Tenant',
							path: '/settings/manage',
						},
					],
				},
			];
		}
	}, [isAdmin]);

	const initials = useMemo(() => {
		if (!auth?.user?.name) return '';
		const inarr = auth.user.name.split(' ');
		if (inarr.length > 1) {
			return (
				inarr[0].trim().substring(0, 1).toUpperCase() +
				inarr[1].trim().substring(0, 1).toUpperCase()
			);
		}
		return inarr[0].trim().substring(0, 2).toUpperCase();
	}, [auth]);

	useEffect(() => {
		const fetchConfig = async () => {
			try {
				const config = await getAppConfig();
				setAppConfig(config);
			} catch (error) {
				console.error('Error fetching app config:', error);
			}
		};
		fetchConfig();
	}, []);

	const [removeAccessStatus] = useApiRequest(
		() =>
			removeAccess &&
			api.deleteSupportDelegateToken(supportDelegate.id).then(() => {
				setRefreshDelegate(refreshDelegate + 1);
			}),
		[removeAccess],
	);

	if (auth == null) {
		return <div>Waiting for user information.....</div>;
	}
	const accessToken: any = jwtDecode(auth.oidcUser.access_token);
	const act = accessToken.act != undefined;

	const profileMenus = [];
	if (!act) {
		profileMenus.push({
			name: supportDelegate ? 'Disable Access' : 'Enable Support Access',
			onClick: () => {
				if (supportDelegate) {
					setRemoveAccess(removeAccess + 1);
				} else {
					setShowPopup(true);
				}
			},
			icon: <SupportAgentIcon />,
		});
	}
	profileMenus.push({
		name: 'Log out',
		onClick: () => {
			location.href = config.logoutURL;
		},
		icon: <ExitToApp />,
	});

	return (
		<BDSProvider>
			<div
				style={{
					display: 'flex',
					height: '100%',
					width: '100%',
					overflowX: 'hidden',
				}}
			>
				<NavBarContainer
					mainMenu={mainMenu}
					profileMenus={profileMenus}
					initials={initials}
					env={appConfig?.codeusEnvironment}
				/>
				<div
					className={classNames(
						classes['flex'],
						classes['z_index_0'],
					)}
					style={{
						overflowY: 'auto',
						overflowX: 'hidden',
						minWidth: 0,
					}}
				>
					<SupportPopup
						show={showPopup}
						closePopup={(refresh) => {
							setShowPopup(false);
							if (refresh) {
								setRefreshDelegate(refreshDelegate + 1);
							}
						}}
					></SupportPopup>
					{supportDelegate && !act ? (
						<Alert
							severity="warning"
							className={classNames(classes['align_center'])}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
								}}
							>
								<Typography color="textPrimary">
									Barracuda Support can access your account
								</Typography>
								<Button
									size="small"
									variant="contained"
									color="primary"
									sx={{ marginLeft: '16px' }}
									className={classNames(classes['d_inline'])}
									isLoading={removeAccessStatus === 'RUNNING'}
									onClick={() => {
										setRemoveAccess(removeAccess + 1);
									}}
								>
									Disable Access
								</Button>
							</Box>
						</Alert>
					) : null}

					{act ? (
						<Alert
							severity="warning"
							className={classNames(classes['align_center'])}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
								}}
							>
								<Typography color="textPrimary">
									Impersonating {auth.user.name} account
								</Typography>
								<Button
									size="small"
									variant="contained"
									color="primary"
									sx={{ marginLeft: '16px' }}
									className={classNames(classes['d_inline'])}
									onClick={() => {
										location.href = config.logoutURL;
									}}
								>
									Logout
								</Button>
							</Box>
						</Alert>
					) : null}

					<AppLayout env={appConfig?.codeusEnvironment}>
						<Outlet />
					</AppLayout>
				</div>
			</div>
		</BDSProvider>
	);
}

export default SideNav;
